import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { RelationOption } from '@/features/manual-graph/relation-options'

export interface UseGraphConnectionOptions {
  onCreateConnection: (
    sourceId: string,
    targetId: string,
    sourceName: string,
    targetName: string,
    relationshipType: string
  ) => Promise<void>
}

export interface ConnectionEntities {
  source: { id: string; name: string; typeLabel?: string; labels?: string[] }
  target: { id: string; name: string; typeLabel?: string; labels?: string[] }
}

export function useGraphConnection({ onCreateConnection }: UseGraphConnectionOptions) {
  const { t } = useTranslation()
  const [isConnectionMode, setIsConnectionMode] = useState(false)
  const [connectionSourceId, setConnectionSourceId] = useState<string | null>(null)
  const [isCreatingConnection, setIsCreatingConnection] = useState(false)
  const [showRelationshipDialog, setShowRelationshipDialog] = useState(false)
  const [pendingConnection, setPendingConnection] = useState<ConnectionEntities | null>(null)

  const startConnectionMode = useCallback((sourceNodeId: string) => {
    setIsConnectionMode(true)
    setConnectionSourceId(sourceNodeId)
  }, [])

  const cancelConnectionMode = useCallback(() => {
    setIsConnectionMode(false)
    setConnectionSourceId(null)
    setPendingConnection(null)
    setShowRelationshipDialog(false)
  }, [])

  const handleConnectionSelect = useCallback(
    (
      targetId: string,
      targetName: string,
      targetTypeLabel: string | undefined,
      targetLabels: string[] | undefined,
      getSourceName: (id: string) => string,
      getSourceTypeLabel: (id: string) => string | undefined,
      getSourceLabels: (id: string) => string[] | undefined
    ) => {
      if (!connectionSourceId || connectionSourceId === targetId) return

      const sourceName = getSourceName(connectionSourceId)
      const sourceTypeLabel = getSourceTypeLabel(connectionSourceId)
      const sourceLabels = getSourceLabels(connectionSourceId)

      // Set pending connection and show dialog
      setPendingConnection({
        source: {
          id: connectionSourceId,
          name: sourceName,
          typeLabel: sourceTypeLabel,
          labels: sourceLabels,
        },
        target: {
          id: targetId,
          name: targetName,
          typeLabel: targetTypeLabel,
          labels: targetLabels,
        },
      })
      setShowRelationshipDialog(true)
    },
    [connectionSourceId]
  )

  // A reversed option is a schema pattern that runs from the picked target to
  // the picked source: the edge is written the way the schema declares it.
  const createRelationship = useCallback(
    async (option: RelationOption) => {
      if (!pendingConnection) return
      const { source, target } =
        option.direction === 'reverse'
          ? { source: pendingConnection.target, target: pendingConnection.source }
          : pendingConnection

      setIsCreatingConnection(true)
      try {
        await onCreateConnection(source.id, target.id, source.name, target.name, option.type)

        toast.success(
          t('manualGraph.relationship.created', {
            type: option.type,
            source: source.name,
            target: target.name,
          })
        )
        setIsConnectionMode(false)
        setConnectionSourceId(null)
        setPendingConnection(null)
        setShowRelationshipDialog(false)
      } catch (_err) {
        toast.error(t('manualGraph.relationship.failed'))
      } finally {
        setIsCreatingConnection(false)
      }
    },
    [pendingConnection, onCreateConnection, t]
  )

  return {
    isConnectionMode,
    connectionSourceId,
    isCreatingConnection,
    showRelationshipDialog,
    setShowRelationshipDialog,
    pendingConnection,
    startConnectionMode,
    cancelConnectionMode,
    handleConnectionSelect,
    createRelationship,
  }
}
